<template>
  <v-card class="pa-6">
    <v-card-title class="text-uppercase">
      {{ isEditing ? 'Editar clínica' : 'Nueva clínica Droopy' }}
    </v-card-title>
    <hr class="my-3" />
    <v-card-text>
      <v-form ref="clinicForm" v-model="formValid">
        <v-row class="my-1">
          <v-col cols="12">
            <v-text-field
              label="Nombre de la clínica"
              v-model="clinicForm.name"
              :rules="nameRules"
              required
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="my-1" v-if="!isEditing">
          <v-col cols="6">
            <v-text-field
              label="RUT"
              v-model="clinicForm.rut"
              :rules="rutRules"
              required
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              label="Email del administrador"
              v-model="clinicForm.adminEmail"
              :rules="emailRules"
              required
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>
      <template v-if="isEditing">
        <v-divider class="my-4"></v-divider>
        <clinic-bank-details-section
          :clinic-id="clinic.clinicId"
          :clinic-name="clinic.clinicName"
        ></clinic-bank-details-section>
      </template>
    </v-card-text>
    <hr class="my-3" />
    <v-card-actions class="justify-end">
      <v-btn text color="primary" rounded @click="closeDialog">Cancelar</v-btn>
      <v-btn :disabled="!formValid || saving" :loading="saving" color="primary" rounded @click="save">
        {{ isEditing ? 'Guardar' : 'Crear cuenta de administrador' }}
      </v-btn>
    </v-card-actions>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import UsersService from '../services/users.service';
import ClinicsService from '../services/clinics.service';
import RutUtils from '../utils/rut.utils';
import { utilsMixin } from '../mixins/utilsMixin';
import ClinicBankDetailsSection from './ClinicBankDetailsSection.vue';

// El backend ya valida NAME_REQUIRED/RUT_REQUIRED/INVALID_RUT_FORMAT/ADMIN_EMAIL_REQUIRED
// (ver 063-clinic-admin-onboarding, pawer-backend) — este diccionario solo traduce esos
// códigos, mismo patrón que src/views/ClinicOnboarding.vue.
const CREATE_CLINIC_ERROR_MESSAGES = {
  NAME_REQUIRED: 'El nombre de la clínica es obligatorio.',
  RUT_REQUIRED: 'El RUT es obligatorio.',
  INVALID_RUT_FORMAT: 'El RUT ingresado no tiene un formato válido.',
  ADMIN_EMAIL_REQUIRED: 'El email del administrador es obligatorio.'
};

export default {
  name: 'DroopyClinicMaintainer',

  components: { ClinicBankDetailsSection },

  mixins: [utilsMixin],

  props: {
    clinic: { type: Object, default: null }
  },

  data() {
    return {
      formValid: false,
      saving: false,
      clinicForm: this.emptyForm(),
      nameRules: [v => !!v || 'El nombre es requerido'],
      rutRules: [
        v => !!v || 'El RUT es requerido',
        v => RutUtils.validateRut(v) || 'RUT no válido'
      ],
      emailRules: [
        v => !!v || 'El email es requerido',
        v => /.+@.+\..+/.test(v) || 'Debe ser un email válido'
      ],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  computed: {
    isEditing() {
      return this.clinic !== null;
    }
  },

  watch: {
    clinic() {
      this.clinicForm = this.emptyForm();
      this.$nextTick(() => {
        if (this.$refs.clinicForm) this.$refs.clinicForm.resetValidation();
      });
    }
  },

  methods: {
    emptyForm() {
      if (this.clinic) {
        return { name: this.clinic.clinicName ?? '', rut: '', adminEmail: '' };
      }
      return { name: '', rut: '', adminEmail: '' };
    },

    closeDialog() {
      this.$emit('close-dialog');
    },

    async save() {
      if (!this.$refs.clinicForm.validate()) return;

      this.saving = true;
      const response = this.isEditing ? await this.updateClinic() : await this.createClinic();
      this.saving = false;

      if (response.status === 'ok') {
        this.closeDialog();
      } else {
        this.showErrorSnackbar(this.mapCreateClinicError(response));
      }
    },

    async createClinic() {
      return UsersService.createManualUser('clinic', { ...this.clinicForm });
    },

    async updateClinic() {
      return ClinicsService.updateClinicName(this.clinic.clinicId, this.clinicForm.name);
    },

    mapCreateClinicError(response) {
      if (response.httpCode === 409) {
        return 'Esta clínica ya tiene una cuenta de administrador.';
      }
      return (
        CREATE_CLINIC_ERROR_MESSAGES[response.message] ||
        (this.isEditing
          ? 'Ocurrió un error al actualizar el nombre. Intenta nuevamente.'
          : 'Ocurrió un error al crear la clínica. Intenta nuevamente.')
      );
    }
  }
};
</script>
